import type { EintragVerortung, PlanCoordinates, ProjectPlan, ProjektVerortung, VerortungPlan } from '../../types';
import { ebeneSchaetzen, type Ebene, type EbenenSchaetzung } from './ebenen';
import { istImPlan, kompassZuPlanwinkel, meterZuPlanProzent, planZuUtm, utmZuPlan, type Georeferenz } from './georeferenz';
import { geschossNameNormalisieren } from './geschoss';
import { gpsMitteln, type GpsFix } from './gpsMittel';
import { druckMedian, type DruckProbe } from './hoehe';
import { planFuerEbene, planGeoreferenz } from './projektVerortung';
import type { VerortungsSitzung } from './sitzung';
import { utmZuWgs84, wgs84ZuUtm } from './utm';

/**
 * Ortsvorschlag aus den Sensoren (docs/verortung-kamera-vertrag.md, Paket A;
 * Plan 9.3): Ebene aus Luftdruck → Plan; Pin aus GPS auf georeferenziertem
 * Plan; Blick aus Kompass. Der Vorschlag überschreibt nie still — die
 * Oberfläche zeigt ihn als Karte mit „Übernehmen“.
 *
 * Reine Funktionen, nichts wirft.
 */

/** Sensorwerte in EINEM Moment (Auslöser, Einblendung). */
export interface SensorSchnappschuss {
    t: number;
    druckHpa: number | null;
    position: { lat: number; lng: number; genauigkeitM: number } | null;
    richtung: { azimutGrad: number; deklinationGrad: number | null; genauigkeit: 0 | 1 | 2 | 3 } | null;
}

/** Median-Fenster des Drucks (filtert Windböen und Türenschlagen). */
const DRUCK_FENSTER_MS = 10_000;
/** GPS: Fixe dieses Zeitraums werden gemittelt. */
const GPS_FENSTER_MS = 5_000;
/** Kompass: ältere Richtung gilt als veraltet (Gerät wurde gedreht). */
const RICHTUNG_MAX_ALTER_MS = 2_000;
/** Pin erst ab dieser GPS-Genauigkeit (Plan 9.3). */
const MAX_GPS_GENAUIGKEIT_M = 8;
/** Pin knapp neben dem Planbild (bis 5 % der Breite/Höhe) wird auf den Rand gesetzt. */
const PIN_RAND_PROZENT = 5;
/** Blick erst ab Android-Kompassgenauigkeit „mittel“. */
const MIN_KOMPASS_GENAUIGKEIT = 2;

const HIMMELSRICHTUNGEN = ['N', 'NO', 'O', 'SO', 'S', 'SW', 'W', 'NW'] as const;

function endlich(x: unknown): x is number {
    return typeof x === 'number' && Number.isFinite(x);
}

/** Winkel auf [0, 360); Rundungsreste knapp unter 360 werden 0. 360 nur bei negativen Winkeln addieren (42,2 bleibt 42,2). */
function gradNormalisieren(grad: number): number {
    let g = grad % 360;
    if (g < 0) g += 360;
    return g > 360 - 1e-7 ? 0 : g;
}

/** 8-teilige Himmelsrichtung (N, NO, O, SO, S, SW, W, NW) zu einem wahren Azimut. */
function himmelsrichtung(grad: number): string {
    return HIMMELSRICHTUNGEN[Math.round(gradNormalisieren(grad) / 45) % 8];
}

function runden(x: number, stellen: number): number {
    const f = 10 ** stellen;
    const r = Math.round(x * f) / f;
    return r === 0 ? 0 : r;
}

/**
 * Sensorwerte für einen Moment `jetzt`: Druck = Median der letzten 10 s;
 * Position = GPS-Mittel (gpsMitteln) der Fixe, die in den letzten 5 s
 * EMPFANGEN wurden (Geräteuhr, nicht GNSS-Zeit); Richtung nur, wenn sie
 * höchstens 2 s alt ist.
 */
export function schnappschussBilden(
    druckProben: readonly DruckProbe[],
    positionen: readonly { lat: number; lng: number; genauigkeitM: number; empfangen: number }[],
    richtung: { azimutGrad: number; deklinationGrad: number | null; genauigkeit: 0 | 1 | 2 | 3; t: number } | null,
    jetzt: number,
): SensorSchnappschuss {
    const druck = druckMedian(Array.isArray(druckProben) ? druckProben : [], jetzt, DRUCK_FENSTER_MS);

    const fixe: GpsFix[] = [];
    for (const p of Array.isArray(positionen) ? positionen : []) {
        if (!p || !endlich(p.empfangen) || p.empfangen < jetzt - GPS_FENSTER_MS) continue;
        fixe.push({ lat: p.lat, lng: p.lng, genauigkeitM: p.genauigkeitM, t: p.empfangen });
    }
    const mittel = fixe.length > 0 ? gpsMitteln(fixe) : null;

    const frisch = !!richtung && endlich(richtung.t) && endlich(richtung.azimutGrad) && jetzt - richtung.t <= RICHTUNG_MAX_ALTER_MS;

    return {
        t: jetzt,
        druckHpa: druck ? druck.hPa : null,
        position: mittel ? { lat: mittel.lat, lng: mittel.lng, genauigkeitM: mittel.genauigkeitM } : null,
        richtung:
            frisch && richtung
                ? {
                      azimutGrad: richtung.azimutGrad,
                      deklinationGrad: endlich(richtung.deklinationGrad) ? richtung.deklinationGrad : null,
                      genauigkeit: richtung.genauigkeit,
                  }
                : null,
    };
}

export interface Ortsvorschlag {
    schaetzung: EbenenSchaetzung | null;
    planId: string | null;
    pin: PlanCoordinates | null;
    pinRadius: { rx: number; ry: number } | null;
    genauigkeitM: number | null;
    blickGradPlan: number | null;
    blickGradWahr: number | null;
    /** „2. OG · ±4 m · Blick NO“, „zwischen 1. OG und 2. OG“, „Gerüst 4. Lage“ */
    text: string;
    quellen: ('Luftdruck' | 'GPS' | 'Kompass')[];
}

function kalibrierung(v: ProjektVerortung, planId: string): VerortungPlan | undefined {
    const plaene = v.plaene;
    if (!plaene || !Object.prototype.hasOwnProperty.call(plaene, planId)) return undefined;
    return plaene[planId];
}

function nurErfolg(g: Georeferenz | { fehler: string } | null): Georeferenz | null {
    return g && !('fehler' in g) ? g : null;
}

/**
 * GPS-Standort auf den Plan. Auslegung „Pin im Plan (Rand 5 %)“: der Punkt
 * darf bis 5 % der Planbreite/-höhe NEBEN dem Bild liegen (istImPlan mit
 * positivem Rand weitet nach außen) und wird dann auf den Bildrand geklemmt;
 * weiter draußen gibt es keinen Pin.
 */
function pinImPlan(geo: Georeferenz, pos: { lat: number; lng: number }): PlanCoordinates | null {
    const utm = wgs84ZuUtm(pos.lat, pos.lng, geo.zone);
    const p = utmZuPlan(geo, utm.ost, utm.nord);
    if (!istImPlan(p, PIN_RAND_PROZENT)) return null;
    return { x: Math.min(100, Math.max(0, p.x)), y: Math.min(100, Math.max(0, p.y)) };
}

/** Ort für die Meridiankonvergenz: GPS (jede Genauigkeit genügt), sonst Planmitte. */
function ortFuerKonvergenz(geo: Georeferenz, pos: SensorSchnappschuss['position']): { lat: number; lng: number } {
    if (pos && endlich(pos.lat) && endlich(pos.lng)) return { lat: pos.lat, lng: pos.lng };
    const mitte = planZuUtm(geo, { x: 50, y: 50 });
    return utmZuWgs84({ zone: geo.zone, ost: mitte.ost, nord: mitte.nord });
}

const LEER: Omit<Ortsvorschlag, 'quellen'> = {
    schaetzung: null,
    planId: null,
    pin: null,
    pinRadius: null,
    genauigkeitM: null,
    blickGradPlan: null,
    blickGradWahr: null,
    text: '',
};

/**
 * Vorschlag aus einem Schnappschuss:
 * - Ebene: ebeneSchaetzen (nur mit Druck; Nullpunkt aus der Sitzung).
 * - Plan: planFuerEbene. Ohne Ebene, aber mit tauglichem GPS: der erste
 *   georeferenzierte Lageplan, auf dem der Standort liegt.
 * - Pin nur bei georeferenziertem Plan, GPS-Genauigkeit ≤ maxGpsGenauigkeitM
 *   (8) und Standort im Plan (Rand 5 %, siehe pinImPlan); pinRadius =
 *   Genauigkeit in Plan-Prozent; genauigkeitM nur mit Pin.
 * - Blick nur bei Kompass-Genauigkeit ≥ 2: blickGradWahr = Azimut +
 *   Deklination; blickGradPlan zusätzlich nur mit Georeferenz des Plans.
 * - quellen: Luftdruck, GPS, Kompass — nur was beigetragen hat.
 * Projekt-Schalter aus (`verortung.aktiv === false`) → leerer Vorschlag.
 */
export function ortsvorschlagBilden(e: {
    verortung: ProjektVerortung;
    plaene: readonly ProjectPlan[];
    sitzung: VerortungsSitzung;
    schnappschuss: SensorSchnappschuss;
    maxGpsGenauigkeitM?: number;
}): Ortsvorschlag {
    const { verortung, sitzung, schnappschuss } = e;
    if (!verortung || verortung.aktiv === false || !schnappschuss) return { ...LEER, quellen: [] };
    const plaene = Array.isArray(e.plaene) ? e.plaene : [];
    const maxGps = endlich(e.maxGpsGenauigkeitM) && e.maxGpsGenauigkeitM > 0 ? e.maxGpsGenauigkeitM : MAX_GPS_GENAUIGKEIT_M;

    const schaetzung =
        endlich(schnappschuss.druckHpa) && sitzung
            ? ebeneSchaetzen(verortung.geschosse ?? [], verortung.geruest, sitzung, schnappschuss.druckHpa, schnappschuss.t)
            : null;
    const ebene = schaetzung?.ebene ?? null;

    const pos = schnappschuss.position;
    const gps =
        pos && endlich(pos.lat) && endlich(pos.lng) && endlich(pos.genauigkeitM) && pos.genauigkeitM >= 0 && pos.genauigkeitM <= maxGps
            ? pos
            : null;

    let plan = planFuerEbene(verortung, plaene, ebene);
    let geo = plan ? nurErfolg(planGeoreferenz(kalibrierung(verortung, plan.id))) : null;
    let pin = gps && geo ? pinImPlan(geo, gps) : null;

    if (!ebene && gps) {
        for (const p of plaene) {
            if (!p || typeof p.id !== 'string') continue;
            const k = kalibrierung(verortung, p.id);
            if (k?.art !== 'lageplan') continue;
            const g = nurErfolg(planGeoreferenz(k));
            const pp = g ? pinImPlan(g, gps) : null;
            if (g && pp) {
                plan = p;
                geo = g;
                pin = pp;
                break;
            }
        }
    }

    const genauigkeitM = pin && gps ? gps.genauigkeitM : null;
    const pinRadius = pin && gps && geo ? meterZuPlanProzent(geo, gps.genauigkeitM) : null;

    let blickGradWahr: number | null = null;
    let blickGradPlan: number | null = null;
    const r = schnappschuss.richtung;
    if (r && endlich(r.azimutGrad) && r.genauigkeit >= MIN_KOMPASS_GENAUIGKEIT) {
        const deklination = endlich(r.deklinationGrad) ? r.deklinationGrad : null;
        blickGradWahr = gradNormalisieren(r.azimutGrad + (deklination ?? 0));
        if (geo) {
            const ort = ortFuerKonvergenz(geo, pos);
            const w = kompassZuPlanwinkel(geo, r.azimutGrad, deklination, ort.lat, ort.lng);
            blickGradPlan = endlich(w) ? w : null;
        }
    }

    const quellen: Ortsvorschlag['quellen'] = [];
    if (schaetzung && (schaetzung.ebene || schaetzung.zwischen)) quellen.push('Luftdruck');
    if (pin) quellen.push('GPS');
    if (blickGradWahr !== null) quellen.push('Kompass');

    const teile: string[] = [];
    if (schaetzung?.zwischen) teile.push(`zwischen ${schaetzung.zwischen[0].name} und ${schaetzung.zwischen[1].name}`);
    else if (ebene) teile.push(ebene.name);
    else if (pin && plan) teile.push((typeof plan.name === 'string' && plan.name.trim()) || 'Lageplan');
    if (genauigkeitM !== null) teile.push(`±${Math.max(1, Math.round(genauigkeitM))} m`);
    if (blickGradWahr !== null) teile.push(`Blick ${himmelsrichtung(blickGradWahr)}`);

    return {
        schaetzung,
        planId: plan ? plan.id : null,
        pin,
        pinRadius,
        genauigkeitM,
        blickGradPlan,
        blickGradWahr,
        text: teile.join(' · '),
        quellen,
    };
}

/** Vergleichsform: normalisiert wie Geschossnamen, „1.OG“ = „1. OG“. */
function vergleichsform(s: string): string {
    return geschossNameNormalisieren(s).replace(/\s*\.\s*/g, '. ').trim();
}

const WORTZEICHEN = /[0-9a-zäöüß]/;

/** Kommt `nadel` als eigenes Wort in `heu` vor? „1. og“ steckt nicht in „11. og“, „eg“ nicht in „weg“. */
function enthaeltAlsWort(heu: string, nadel: string): boolean {
    if (!nadel) return false;
    let ab = heu.indexOf(nadel);
    while (ab >= 0) {
        const vor = ab > 0 ? heu[ab - 1] : '';
        const nach = heu[ab + nadel.length] ?? '';
        const grenzeVorn = !vor || !WORTZEICHEN.test(vor) || !WORTZEICHEN.test(nadel[0]);
        const grenzeHinten = !nach || !WORTZEICHEN.test(nach) || !WORTZEICHEN.test(nadel[nadel.length - 1]);
        if (grenzeVorn && grenzeHinten) return true;
        ab = heu.indexOf(nadel, ab + 1);
    }
    return false;
}

/**
 * Ortstext beim Übernehmen: leer → Ebenenname; enthält den Namen schon
 * (case-insensitiv, als eigenes Wort, „1.OG“ = „1. OG“) → unverändert;
 * sonst „<Ebene> · <bisher>“. Ohne Ebene bleibt der Text, wie er ist.
 */
export function ortstextUebernehmen(bisher: string, ebene: Ebene | null): string {
    const text = typeof bisher === 'string' ? bisher : '';
    const name = ebene && typeof ebene.name === 'string' ? ebene.name.trim() : '';
    if (!name) return text;
    if (!text.trim()) return name;
    if (enthaeltAlsWort(vergleichsform(text), vergleichsform(name))) return text;
    return `${name} · ${text.trim()}`;
}

/**
 * EintragVerortung aus einem Vorschlag (für InspectionItem.verortung).
 * `ebene` = Name der geschätzten Ebene, im Treppenhaus „zwischen 1. OG und
 * 2. OG“; Höhe auf cm, Genauigkeit und Blick auf 0,1, Sicherheit auf 0,01.
 */
export function eintragVerortung(v: Ortsvorschlag): EintragVerortung {
    const s = v?.schaetzung ?? null;
    let ebene: string | null = null;
    if (s?.zwischen) ebene = `zwischen ${s.zwischen[0].name} und ${s.zwischen[1].name}`;
    else if (s?.ebene) ebene = s.ebene.name;
    return {
        quelle: 'sensor',
        ebene,
        hoeheM: s && endlich(s.hoeheM) ? runden(s.hoeheM, 2) : null,
        genauigkeitM: v && endlich(v.genauigkeitM) ? runden(v.genauigkeitM, 1) : null,
        blickGradPlan: v && endlich(v.blickGradPlan) ? gradNormalisieren(runden(gradNormalisieren(v.blickGradPlan), 1)) : null,
        sicherheit: s && endlich(s.sicherheit) ? runden(s.sicherheit, 2) : null,
    };
}
